import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  InputAdornment,
  Grid,
  Drawer,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Phone,
  Edit,
  Delete,
  Visibility,
  Add as AddIcon,
  Close,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { apiService, CallRecord, Customer } from '../../services/apiService';
import Swal from 'sweetalert2';


const CallCenter: React.FC = () => {
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingQuery, setEditingQuery] = useState<CallRecord | null>(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    supplier_id: '',
    agent_name: '',
    call_type: 'inbound',
    duration: '',
    status: 'completed',
    rating: '',
    notes: '',
  });

  // Fetch data from APIs
  useEffect(() => {
    fetchData();
    fetchCustomers();
    fetchSuppliers();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const callsResponse = await apiService.getCallRecords();
      const queriesData = Array.isArray(callsResponse.data) ? callsResponse.data : [];
      const sortedQueries = queriesData.sort((a, b) => b.id - a.id);
      setCalls(sortedQueries);
    } catch (error: any) {
      console.error('Failed to fetch call center data:', error);
      if (error.status === 401) {
        toast.error('Please login to access call center data');
      } else {
        toast.error('Failed to load call center data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await apiService.getCustomers();
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await apiService.getSuppliers();
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const handleOpenDrawer = () => {
    if (customers.length === 0) {
      Swal.fire('Error!', 'No customers available. Please add customers first.', 'error');
      return;
    }
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingQuery(null);
    setFormData({
      customer_id: '',
      supplier_id: '',
      agent_name: '',
      call_type: 'inbound',
      duration: '',
      status: 'completed',
      rating: '',
      notes: '',
    });
  };

  const handleSubmit = async () => {
    if (!formData.customer_id || !formData.agent_name || !formData.duration) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const selectedCustomer = customers.find(c => c.id.toString() === formData.customer_id);
      
      const queryData = {
        agent_name: formData.agent_name,
        customer_name: selectedCustomer?.name || '',
        call_type: formData.call_type as 'inbound' | 'outbound',
        duration: formData.duration,
        status: formData.status as 'completed' | 'missed' | 'ongoing',
        rating: formData.rating ? parseInt(formData.rating) : 0,
        notes: formData.notes,
        agent_id: 1, // Default agent ID since we're not managing agents anymore
        customer_id: parseInt(formData.customer_id),
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : undefined,
        timestamp: new Date().toISOString()
      };

      if (editingQuery) {
        await apiService.updateCallRecord(editingQuery.id, queryData);
        toast.success('Query updated successfully');
      } else {
        await apiService.createCallRecord(queryData);
        toast.success('Query added successfully');
      }
      
      handleCloseDrawer();
      fetchData();
    } catch (error: any) {
      toast.error(editingQuery ? 'Failed to update query' : 'Failed to add query');
    }
  };

  const handleEdit = async (id: number) => {
    const query = calls.find(c => c.id === id);
    if (query) {
      setEditingQuery(query);
      setFormData({
        customer_id: query.customer_id.toString(),
        supplier_id: (query as any).supplier_id?.toString() || '',
        agent_name: query.agent_name || '',
        call_type: query.call_type || 'inbound',
        duration: query.duration || '',
        status: query.status || 'completed',
        rating: query.rating?.toString() || '',
        notes: query.notes || '',
      });
      setDrawerOpen(true);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id: number) => {
    try {
        const call = calls.find(c => c.id === id);
        if (call) {
          const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete call record: ${call.customer_name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
          });

          if (result.isConfirmed) {
            await apiService.deleteCallRecord(id);
            Swal.fire(
              'Deleted!',
              'Call record has been deleted successfully.',
              'success'
            );
            fetchData(); // Refresh data
        }
      }
    } catch (error: any) {
      console.error('Failed to delete record:', error);
      Swal.fire(
        'Error!',
        'Failed to delete record.',
        'error'
      );
    }
  };

  const handleView = async (id: number) => {
    try {
        const call = calls.find(c => c.id === id);
        if (call) {
          await Swal.fire({
            title: 'Call Record Details',
            html: `
              <div style="text-align: left;">
                <p><strong>Agent:</strong> ${call.agent_name}</p>
                <p><strong>Customer:</strong> ${call.customer_name}</p>
                <p><strong>Call Type:</strong> ${call.call_type}</p>
                <p><strong>Duration:</strong> ${call.duration}</p>
                <p><strong>Status:</strong> ${call.status}</p>
                <p><strong>Rating:</strong> ${call.rating}/5</p>
                <p><strong>Timestamp:</strong> ${new Date(call.timestamp).toLocaleString()}</p>
                <p><strong>Notes:</strong> ${call.notes}</p>
              </div>
            `,
            icon: 'info',
            confirmButtonText: 'Close'
          });
      }
    } catch (error) {
      toast.error('Failed to view record');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'busy':
        return 'warning';
      case 'inactive':
        return 'error';
      case 'completed':
        return 'success';
      case 'missed':
        return 'error';
      case 'ongoing':
        return 'info';
      default:
        return 'default';
    }
  };

  const filteredCalls = calls.filter(call => {
    const matchesSearch = call.agent_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         call.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         call.call_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || call.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const currentData = filteredCalls;
  const paginatedData = currentData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography>Loading call center data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1f2937' }}>
          Call Center Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDrawer}
          sx={{
            backgroundColor: '#4A90E2',
            color: 'white',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#357ABD',
            },
          }}
        >
          Add New Query
        </Button>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            backgroundColor: 'white',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Total Queries
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937' }}>
                    {calls.length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  backgroundColor: '#f0f9ff', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Phone sx={{ color: '#4A90E2', fontSize: 24 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            backgroundColor: 'white',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Completed Queries
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                    {calls.filter(call => call.status === 'completed').length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  backgroundColor: '#f0f9ff', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Phone sx={{ color: '#10b981', fontSize: 24 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            backgroundColor: 'white',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Missed Queries
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ef4444' }}>
                    {calls.filter(call => call.status === 'missed').length}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  backgroundColor: '#f0f9ff', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Phone sx={{ color: '#f59e0b', fontSize: 24 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            backgroundColor: 'white',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Average Rating
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                    {calls.length > 0 ? (calls.reduce((sum, call) => sum + (call.rating || 0), 0) / calls.length).toFixed(1) : '0.0'}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  backgroundColor: '#f0f9ff', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Phone sx={{ color: '#8b5cf6', fontSize: 24 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        backgroundColor: 'white',
        mb: 3,
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
              size="small"
              sx={{
                flexGrow: 1,
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  height: 40,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#6b7280' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => handleFilterChange('all')}
              sx={{
                borderColor: '#e5e7eb',
                color: '#6b7280',
                height: 40,
                px: 2,
                textTransform: 'none',
              }}
            >
              Filter
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              sx={{
                borderColor: '#e5e7eb',
                color: '#6b7280',
                height: 40,
                px: 2,
                textTransform: 'none',
              }}
            >
              Export
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        backgroundColor: 'white',
      }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Query ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Agent</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Supplier</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Call Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Notes</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item: any) => (
                  <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                    <TableCell sx={{ color: '#1f2937' }}>#{item.id}</TableCell>
                    <TableCell sx={{ color: '#1f2937' }}>{item.agent_name}</TableCell>
                    <TableCell sx={{ color: '#1f2937' }}>{item.customer_name}</TableCell>
                    <TableCell sx={{ color: '#6b7280' }}>
                      {item.supplier_name || 'N/A'}
                    </TableCell>
                        <TableCell>
                          <Chip
                        label={item.call_type}
                            size="small"
                        sx={{
                          backgroundColor: item.call_type === 'inbound' ? '#e0f2f1' : '#fef3c7',
                          color: item.call_type === 'inbound' ? '#0d9488' : '#f59e0b',
                          textTransform: 'capitalize'
                        }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#6b7280' }}>{item.duration}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.status}
                            color={getStatusColor(item.status) as any}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                    <TableCell sx={{ color: '#1f2937' }}>
                      {item.rating ? `⭐ ${item.rating}/5` : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#6b7280' }}>
                      {item.timestamp ? new Date(item.timestamp).toLocaleString() : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#6b7280', maxWidth: 200 }}>
                      <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.notes || 'No notes'}
                      </Typography>
                    </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton size="small" onClick={() => handleView(item.id)}>
                              <Visibility sx={{ fontSize: 18, color: '#6b7280' }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEdit(item.id)}>
                              <Edit sx={{ fontSize: 18, color: '#6b7280' }} />
                            </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(item.id)}>
                          <Delete sx={{ fontSize: 18, color: '#dc2626' }} />
                        </IconButton>
                          </Box>
                        </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={currentData.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                paddingLeft: 3,
                paddingRight: 3,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add Query Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 600 },
            maxWidth: '100%',
          },
        }}
      >
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
              {editingQuery ? 'Edit Query' : 'Add New Query'}
            </Typography>
            <IconButton onClick={handleCloseDrawer} sx={{ color: '#6b7280' }}>
              <Close />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Form */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  value={formData.customer_id}
                  label="Customer"
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_id: e.target.value }))}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id.toString()}>
                      {customer.name} - {customer.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Supplier (Optional)</InputLabel>
                <Select
                  value={formData.supplier_id}
                  label="Supplier (Optional)"
                  onChange={(e) => setFormData(prev => ({ ...prev, supplier_id: e.target.value }))}
                >
                  <MenuItem value="">None</MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id.toString()}>
                      {supplier.name || supplier.supplier_name} - {supplier.email || 'N/A'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => `${option.name} - ${option.email}`}
                value={customers.find(customer => customer.name === formData.agent_name) || null}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, agent_name: newValue?.name || '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Agent Name"
                    required
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option.name} - {option.email}
                  </Box>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Call Type</InputLabel>
                <Select
                  value={formData.call_type}
                  label="Call Type"
                  onChange={(e) => setFormData(prev => ({ ...prev, call_type: e.target.value }))}
                >
                  <MenuItem value="inbound">Inbound</MenuItem>
                  <MenuItem value="outbound">Outbound</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Duration"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                placeholder="e.g., 5 min"
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="missed">Missed</MenuItem>
                  <MenuItem value="ongoing">Ongoing</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Rating"
                type="number"
                inputProps={{ min: 1, max: 5 }}
                value={formData.rating}
                onChange={(e) => setFormData(prev => ({ ...prev, rating: e.target.value }))}
                placeholder="1-5"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter query notes..."
              />
            </Grid>
          </Grid>

          {/* Footer Actions */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseDrawer}
              sx={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ flex: 1, backgroundColor: '#4A90E2' }}
            >
              {editingQuery ? 'Update Query' : 'Add Query'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default CallCenter;